import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, CreditCard, MessageCircle, LifeBuoy, AlertTriangle, Clock } from 'lucide-react';
import { paymentHoldAPI } from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function PaymentHoldScreen() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    paymentHoldAPI.myStatus()
      .then((r) => {
        setStatus(r.data);
        // Not actually on hold (e.g. released in another tab) → leave the screen.
        if (r.data && r.data.on_hold === false) navigate('/client/home', { replace: true });
      })
      .catch(() => setStatus(null))
      .finally(() => setLoading(false));
  }, [navigate]);

  const amount = status?.outstanding_amount;
  const hasAmount = typeof amount === 'number' && amount > 0;
  const achPending = status?.ach_pending;

  return (
    <div
      data-testid="payment-hold-screen"
      className="min-h-screen bg-slate-950 text-slate-100 flex items-center justify-center px-4 py-12"
    >
      <div className="w-full max-w-lg">
        <div className="rounded-3xl border border-rose-500/30 bg-slate-900/80 shadow-2xl backdrop-blur p-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="h-12 w-12 rounded-2xl bg-rose-500/15 flex items-center justify-center">
              <Lock className="h-6 w-6 text-rose-400" />
            </div>
            <div>
              <h1 className="text-xl font-bold tracking-tight" data-testid="payment-hold-title">
                Account On Hold for Payment
              </h1>
              <p className="text-xs text-rose-300/80 uppercase tracking-widest">
                {achPending ? 'ACH payment pending verification' : 'Outstanding booking payment'}
              </p>
            </div>
          </div>

          <p className="text-sm leading-relaxed text-slate-300" data-testid="payment-hold-message">
            Your iEvntür account has been temporarily placed on hold due to an outstanding
            booking payment or required deposit. Please complete your payment to restore
            access. If you believe this hold was applied in error, please contact support.
          </p>

          {status?.reason && (
            <div className="mt-4 flex items-start gap-2 rounded-xl bg-slate-800/60 p-3 text-sm text-slate-300">
              <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5 shrink-0" />
              <span data-testid="payment-hold-reason">{status.reason}</span>
            </div>
          )}

          {hasAmount && (
            <div className="mt-5 rounded-2xl bg-slate-800/80 p-5 border border-slate-700">
              <p className="text-xs uppercase tracking-widest text-slate-400">Outstanding balance</p>
              <p className="text-3xl font-bold text-white mt-1" data-testid="payment-hold-amount">
                ${Number(amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </p>
              {status?.deadline && (
                <p className="mt-2 flex items-center gap-1.5 text-xs text-amber-300">
                  <Clock className="h-3.5 w-3.5" /> Payment due by {status.deadline}
                </p>
              )}
            </div>
          )}

          {achPending && (
            <div className="mt-4 rounded-xl bg-sky-500/10 border border-sky-500/30 p-3 text-sm text-sky-200">
              Your ACH bank transfer is being verified. Access will be restored automatically
              once funding clears, which can take several business days.
            </div>
          )}

          <div className="mt-7 space-y-3">
            {hasAmount && (
              <button
                data-testid="payment-hold-make-payment-btn"
                onClick={() => navigate('/client/billing')}
                className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-cyan-500 hover:bg-cyan-400 text-slate-950 font-semibold py-3 transition-colors"
              >
                <CreditCard className="h-4 w-4" /> Make Payment
              </button>
            )}

            <div className="grid grid-cols-2 gap-3">
              <button
                data-testid="payment-hold-ask-evie-btn"
                onClick={() => navigate('/client/messages/evie')}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-100 font-medium py-3 transition-colors"
              >
                <MessageCircle className="h-4 w-4 text-cyan-400" /> Ask Evie
              </button>
              <button
                data-testid="payment-hold-support-btn"
                onClick={() => navigate('/client/messages/support')}
                className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-700 bg-slate-800/60 hover:bg-slate-800 text-slate-100 font-medium py-3 transition-colors"
              >
                <LifeBuoy className="h-4 w-4 text-emerald-400" /> Contact Support
              </button>
            </div>

            <button
              data-testid="payment-hold-logout-btn"
              onClick={() => { logout(); navigate('/login'); }}
              className="w-full text-xs text-slate-500 hover:text-slate-300 pt-2 transition-colors"
            >
              Sign out
            </button>
          </div>
        </div>

        {loading && (
          <p className="text-center text-xs text-slate-500 mt-4">Loading your account status…</p>
        )}
      </div>
    </div>
  );
}
